/**
 * Deliverable Service
 * Handles all deliverable-related API calls including CRUD operations,
 * status management, completion tracking, and filtering
 */

import { apiClient, buildQueryParams, ApiResponse } from './api.client';
import { unwrapListResponse } from './api.unwrap';
import {
  Deliverable,
  CreateDeliverableRequest,
  UpdateDeliverableRequest,
  CompleteDeliverableRequest,
  ListDeliverablesRequest,
  PaginatedResponse,
  DeliverableStatus,
  ContentPlatform,
} from '@creatoros/shared';

/**
 * Deliverable statistics interface
 */
export interface DeliverableStatistics {
  total: number;
  completed: number;
  pending: number;
  inProgress: number;
  overdue: number;
  byPlatform: Record<ContentPlatform, number>;
  byStatus: Record<DeliverableStatus, number>;
}

/**
 * Deliverable Service Class
 */
class DeliverableService {
  private readonly basePath = '/api/deliverables';

  /**
   * Get all deliverables with optional filtering and pagination
   * @param params - List parameters including filters and pagination
   * @returns Paginated list of deliverables
   */
  async list(params?: ListDeliverablesRequest): Promise<PaginatedResponse<Deliverable>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get(this.basePath, {
      params: queryParams,
    });

    return unwrapListResponse<Deliverable>(
      response.data
    ) as unknown as PaginatedResponse<Deliverable>;
  }

  /**
   * Get a single deliverable by ID
   * @param id - Deliverable ID
   * @returns Deliverable data
   */
  async getById(id: string): Promise<Deliverable> {
    const response = await apiClient.get<ApiResponse<Deliverable>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch deliverable');
  }

  /**
   * Create a new deliverable
   * @param data - Deliverable creation data
   * @returns Created deliverable
   */
  async create(data: CreateDeliverableRequest): Promise<Deliverable> {
    const response = await apiClient.post<ApiResponse<Deliverable>>(this.basePath, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to create deliverable');
  }

  /**
   * Update an existing deliverable
   * @param id - Deliverable ID
   * @param data - Deliverable update data
   * @returns Updated deliverable
   */
  async update(id: string, data: UpdateDeliverableRequest): Promise<Deliverable> {
    const response = await apiClient.patch<ApiResponse<Deliverable>>(
      `${this.basePath}/${id}`,
      data
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update deliverable');
  }

  /**
   * Delete a deliverable
   * @param id - Deliverable ID
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  /**
   * Mark deliverable as completed
   * @param id - Deliverable ID
   * @param data - Completion data including notes and attachments
   * @returns Updated deliverable
   */
  async complete(id: string, data?: CompleteDeliverableRequest): Promise<Deliverable> {
    const response = await apiClient.post<ApiResponse<Deliverable>>(
      `${this.basePath}/${id}/complete`,
      data
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to complete deliverable');
  }

  /**
   * Update deliverable status
   * @param id - Deliverable ID
   * @param status - New status
   * @returns Updated deliverable
   */
  async updateStatus(id: string, status: DeliverableStatus): Promise<Deliverable> {
    return this.update(id, { status });
  }

  /**
   * Get deliverables by deal
   * @param dealId - Deal ID to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deliverables for specified deal
   */
  async getByDeal(
    dealId: string,
    params?: Omit<ListDeliverablesRequest, 'dealId'>
  ): Promise<PaginatedResponse<Deliverable>> {
    return this.list({
      ...params,
      dealId,
    });
  }

  /**
   * Get deliverables by status
   * @param status - Status to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deliverables with specified status
   */
  async getByStatus(
    status: DeliverableStatus,
    params?: Omit<ListDeliverablesRequest, 'status'>
  ): Promise<PaginatedResponse<Deliverable>> {
    return this.list({
      ...params,
      status,
    });
  }

  /**
   * Get deliverables by platform
   * @param platform - Platform to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deliverables for specified platform
   */
  async getByPlatform(
    platform: ContentPlatform,
    params?: Omit<ListDeliverablesRequest, 'platform'>
  ): Promise<PaginatedResponse<Deliverable>> {
    return this.list({
      ...params,
      platform,
    });
  }

  /**
   * Get overdue deliverables
   * @param params - Additional list parameters
   * @returns Paginated list of overdue deliverables
   */
  async getOverdue(
    params?: Omit<ListDeliverablesRequest, 'status'>
  ): Promise<PaginatedResponse<Deliverable>> {
    const response = await apiClient.get<PaginatedResponse<Deliverable>>(
      `${this.basePath}/overdue`,
      { params: params ? buildQueryParams(params as Record<string, unknown>) : {} }
    );

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch overdue deliverables');
  }

  /**
   * Get upcoming deliverables (due soon)
   * @param days - Number of days to look ahead (default: 7)
   * @param params - Additional list parameters
   * @returns Paginated list of upcoming deliverables
   */
  async getUpcoming(
    days: number = 7,
    params?: Omit<ListDeliverablesRequest, 'status'>
  ): Promise<PaginatedResponse<Deliverable>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get<PaginatedResponse<Deliverable>>(
      `${this.basePath}/upcoming`,
      { params: { ...queryParams, days } }
    );

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch upcoming deliverables');
  }

  /**
   * Get deliverable statistics
   * @param filters - Optional filters for statistics
   * @returns Deliverable statistics
   */
  async getStats(filters?: {
    dealId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<DeliverableStatistics> {
    const queryParams = filters ? buildQueryParams(filters as Record<string, unknown>) : {};

    const response = await apiClient.get<ApiResponse<DeliverableStatistics>>(
      `${this.basePath}/stats`,
      { params: queryParams }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch deliverable statistics');
  }

  /**
   * Upload attachment for deliverable
   * @param id - Deliverable ID
   * @param file - File to upload
   * @returns Updated deliverable with attachment
   */
  async uploadAttachment(id: string, file: File): Promise<Deliverable> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ApiResponse<Deliverable>>(
      `${this.basePath}/${id}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to upload attachment');
  }

  /**
   * Remove attachment from deliverable
   * @param id - Deliverable ID
   * @param attachmentUrl - URL of attachment to remove
   * @returns Updated deliverable
   */
  async removeAttachment(id: string, attachmentUrl: string): Promise<Deliverable> {
    const response = await apiClient.delete<ApiResponse<Deliverable>>(
      `${this.basePath}/${id}/attachments`,
      {
        data: { attachmentUrl },
      }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to remove attachment');
  }

  /**
   * Duplicate a deliverable
   * @param id - Deliverable ID to duplicate
   * @returns New duplicated deliverable
   */
  async duplicate(id: string): Promise<Deliverable> {
    const response = await apiClient.post<ApiResponse<Deliverable>>(
      `${this.basePath}/${id}/duplicate`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to duplicate deliverable');
  }

  /**
   * Bulk update deliverable status
   * @param ids - Array of deliverable IDs
   * @param status - New status
   * @returns Array of updated deliverables
   */
  async bulkUpdateStatus(ids: string[], status: DeliverableStatus): Promise<Deliverable[]> {
    const response = await apiClient.patch<ApiResponse<Deliverable[]>>(
      `${this.basePath}/bulk/status`,
      { ids, status }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to bulk update deliverables');
  }

  /**
   * Bulk delete deliverables
   * @param ids - Array of deliverable IDs to delete
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/bulk`, {
      data: { ids },
    });
  }
}

/**
 * Export singleton instance
 */
export const deliverableService = new DeliverableService();

/**
 * Export default
 */
export default deliverableService;
